import type { Backend, RawQuery } from '@freelancer/datastore/core';
import {
  OrderByDirection,
  getQueryParamValue,
} from '@freelancer/datastore/core';
import { isArray, toNumber } from '@freelancer/utils';
import type { TaskGroup } from './task-group.model';
import { transformLinkedSourcesToSnakeCase } from './task-group.transformers';
import type { TaskGroupCollection } from './task-group.types';

export function taskGroupBackend(): Backend<TaskGroupCollection> {
  return {
    defaultOrder: {
      field: 'id',
      direction: OrderByDirection.ASC,
    },
    fetch: (authUid, ids, query, order) => ({
      endpoint: 'tasks/0.1/tasks/groups',
      isGaf: false,
      params: {
        id: ids,
        owner_id: getQueryParamValue(query, 'ownerId'),
        status: getQueryParamValue(query, 'status'),
        linked_sources: transformSourceLinkFetchParam(query),
        user_ids: [authUid],
        workflow_types: getQueryParamValue(query, 'workflowType'),
      },
    }),
    push: (authUid, taskGroup) => ({
      endpoint: 'tasks/0.1/tasks/groups',
      method: 'POST',
      payload: {
        owner_id: toNumber(authUid),
        linked_sources: transformLinkedSourcesToSnakeCase(
          taskGroup.linkedSources,
        ),
        name: taskGroup.name,
        description: taskGroup.description,
      },
      extractId: response => response.task_group.id,
    }),
    set: undefined,
    update: (authUid, partial, original) => ({
      endpoint: `tasks/0.1/tasks/groups/${original.id}`,
      method: 'PUT',
      payload: {
        id: original.id,
        status: partial.status,
        name: partial.name,
        description: partial.description,
        linked_sources: partial.linkedSources
          ? transformLinkedSourcesToSnakeCase(partial.linkedSources)
          : undefined,
      },
    }),
    remove: (authUid, id, document) => ({
      endpoint: `tasks/0.1/tasks/groups/${id}`,
      method: 'DELETE',
      payload: {},
    }),
  };
}

export function transformSourceLinkFetchParam(
  query: RawQuery<TaskGroup> | undefined,
): readonly string[] | undefined {
  const sourceLinks = getQueryParamValue(query, 'linkedSourcesMinimal')[0];
  // Note that the API requires this specific format and ordering
  return isArray(sourceLinks)
    ? sourceLinks.map(
        sourceLink => `${sourceLink.sourceId}-${sourceLink.sourceType}`,
      )
    : undefined;
}
